import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SoulEditor from '../components/agents/SoulEditor';
import ReasoningTraceViewer from '../components/agents/ReasoningTraceViewer';
import VoiceButton from '../components/ui/VoiceButton';
import {
    Bot, Save, ArrowLeft, Loader2, Brain, Wrench, Database, Zap,
    Shield, FlaskConical, CheckCircle2, XCircle, Eye, EyeOff,
    Play, Trash2, RefreshCw, Clock, AlertTriangle, Info, ChevronDown, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
    { id: 'soul', label: 'Soul', icon: Brain },
    { id: 'tools', label: 'Tools', icon: Wrench },
    { id: 'data', label: 'Data Access', icon: Database },
    { id: 'triggers', label: 'Triggers', icon: Zap },
    { id: 'governance', label: 'Governance', icon: Shield },
    { id: 'sandbox', label: 'Sandbox', icon: FlaskConical },
];

const TRIGGER_TYPES = [
    { value: 'manual', label: 'Manual', description: 'Triggered by user or API call' },
    { value: 'contact_created', label: 'Contact Created', description: 'When a new contact is added' },
    { value: 'contact_updated', label: 'Contact Updated', description: 'When a contact field changes' },
    { value: 'deal_stage_changed', label: 'Deal Stage Changed', description: 'When a deal moves to a new stage' },
    { value: 'deal_created', label: 'Deal Created', description: 'When a new deal is created' },
    { value: 'activity_created', label: 'Activity Created', description: 'When a task, note, or call is logged' },
    { value: 'campaign_enrolled', label: 'Campaign Enrolled', description: 'When a contact enters a campaign' },
    { value: 'form_submitted', label: 'Form Submitted', description: 'When a form submission is received' },
    { value: 'periodic', label: 'Periodic / Scheduled', description: 'Runs on a recurring schedule' },
];

const DATA_COLLECTIONS = [
    { name: 'contacts', label: 'Contacts', category: 'CRM' },
    { name: 'accounts', label: 'Companies', category: 'CRM' },
    { name: 'deals', label: 'Deals', category: 'CRM' },
    { name: 'activities', label: 'Activities', category: 'CRM' },
    { name: 'communications', label: 'Communications', category: 'Communication' },
    { name: 'campaigns', label: 'Campaigns', category: 'Marketing' },
    { name: 'forms', label: 'Forms', category: 'Marketing' },
    { name: 'products', label: 'Products', category: 'Products' },
    { name: 'proposals', label: 'Proposals', category: 'Sales' },
    { name: 'sequences', label: 'Email Sequences', category: 'Marketing' },
    { name: 'custom_modules', label: 'Custom Modules', category: 'ERP' },
];

export default function AgentBuilder() {
    const { agentId } = useParams();
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();

    const [agent, setAgent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('soul');
    const [availableTools, setAvailableTools] = useState([]);
    const [dirty, setDirty] = useState(false);

    // Agent form fields
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [soulMd, setSoulMd] = useState('');
    const [enabledTools, setEnabledTools] = useState([]);
    const [dataAccess, setDataAccess] = useState({ read: [], write: [] });
    const [triggers, setTriggers] = useState([]);
    const [governance, setGovernance] = useState({
        requires_approval: [],
        max_actions_per_run: 20,
        max_runs_per_hour: 10,
        max_cost_per_day_usd: 5.0,
        auto_pause_on_error_count: 3,
    });
    const [modelConfig, setModelConfig] = useState({
        model: 'gpt-4o',
        temperature: 0.3,
        max_tokens: 4096,
    });

    // Magic AI state
    const [generating, setGenerating] = useState(false);

    // Sandbox state
    const [sandboxRunning, setSandboxRunning] = useState(false);
    const [sandboxRun, setSandboxRun] = useState(null);
    const [runs, setRuns] = useState([]);
    const [sandboxInput, setSandboxInput] = useState('');

    useEffect(() => {
        loadAgent();
        loadTools();
    }, [agentId]);

    const loadAgent = async () => {
        try {
            const res = await axios.get(`${API}/agents/${agentId}`, { headers: getAuthHeader() });
            const a = res.data;
            setAgent(a);
            setName(a.name || '');
            setDescription(a.description || '');
            setSoulMd(a.soul_md || '');
            setEnabledTools(a.enabled_tools || []);
            setDataAccess(a.data_access || { read: [], write: [] });
            setTriggers(a.triggers || []);
            setGovernance(a.governance || governance);
            setModelConfig(a.model_config_settings || modelConfig);
            loadRuns();
        } catch (err) {
            toast.error('Failed to load agent');
            navigate('/agents');
        } finally {
            setLoading(false);
        }
    };

    const loadTools = async () => {
        try {
            const res = await axios.get(`${API}/agents/tools`, { headers: getAuthHeader() });
            setAvailableTools(res.data.tools || []);
        } catch (err) {
            console.error('Failed to load tools', err);
        }
    };

    const loadRuns = async () => {
        try {
            const res = await axios.get(`${API}/agents/${agentId}/runs`, { headers: getAuthHeader() });
            setRuns(res.data.runs || []);
        } catch (err) {
            console.error('Failed to load runs', err);
        }
    };

    const markDirty = useCallback((setter) => {
        return (...args) => {
            setDirty(true);
            setter(...args);
        };
    }, []);

    const saveAgent = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/agents/${agentId}`, {
                name,
                description,
                soul_md: soulMd,
                enabled_tools: enabledTools,
                data_access: dataAccess,
                triggers,
                governance,
                model_config_settings: modelConfig,
            }, { headers: getAuthHeader() });
            toast.success('Agent saved');
            setDirty(false);
            loadAgent();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save agent');
        } finally {
            setSaving(false);
        }
    };

    const generateSoulWithAI = async () => {
        if (!name.trim() || name.trim() === 'New Agent') {
            toast.error('Give your agent a name and description first');
            return;
        }
        setGenerating(true);
        try {
            const res = await axios.post(`${API}/ai/chat`, {
                message: `You are an expert at designing AI agent soul files (system prompts) for a CRM/ERP platform called NurtureCRM.

Generate a comprehensive markdown soul file for an AI agent with:
- Name: "${name}"
- Description: "${description || 'No description provided'}"
- Enabled tools: ${enabledTools.length > 0 ? enabledTools.join(', ') : 'none configured yet'}
- Data access (read): ${dataAccess.read?.join(', ') || 'none'}
- Data access (write): ${dataAccess.write?.join(', ') || 'none'}

The soul file should include:
1. **Identity** — Who the agent is, its role and expertise
2. **Goals** — Primary objectives and success criteria
3. **Constraints** — What the agent must NOT do, safety boundaries
4. **Communication Style** — Tone, formality, how it addresses users
5. **Decision Framework** — How it prioritizes and makes choices
6. **Workflow** — Step-by-step approach for its primary tasks

Write ONLY the markdown content, no code fences, no preamble. Use ## headers for sections. Be specific to CRM/ERP operations and sales workflows. Keep it practical and actionable.`,
                context: 'agent_builder',
            }, { headers: getAuthHeader() });
            const generated = res.data.response || res.data.message || '';
            if (generated) {
                setSoulMd(generated);
                setDirty(true);
                toast.success('Soul file generated! Review and customize it.');
            }
        } catch (err) {
            toast.error('Failed to generate soul file');
        } finally {
            setGenerating(false);
        }
    };

    const runSandbox = async () => {
        setSandboxRunning(true);
        setSandboxRun(null);
        try {
            const payload = {};
            if (sandboxInput.trim()) {
                payload.trigger_data = { message: sandboxInput.trim() };
            }
            const res = await axios.post(
                `${API}/agents/${agentId}/sandbox/simulate`,
                payload,
                { headers: getAuthHeader() }
            );
            setSandboxRun(res.data);
            toast.success('Sandbox run completed');
            loadRuns();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Sandbox simulation failed');
        } finally {
            setSandboxRunning(false);
        }
    };

    const toggleTool = (toolName) => {
        setDirty(true);
        setEnabledTools(prev =>
            prev.includes(toolName)
                ? prev.filter(t => t !== toolName)
                : [...prev, toolName]
        );
    };

    const toggleDataAccess = (collection, mode) => {
        setDirty(true);
        setDataAccess(prev => {
            const current = prev[mode] || [];
            const updated = current.includes(collection)
                ? current.filter(c => c !== collection)
                : [...current, collection];
            return { ...prev, [mode]: updated };
        });
    };

    const addTrigger = (triggerType) => {
        setDirty(true);
        setTriggers(prev => [
            ...prev,
            {
                trigger_id: `trig_${Date.now()}`,
                trigger_type: triggerType,
                config: {},
                filter: {},
                is_active: true,
            },
        ]);
    };

    const removeTrigger = (triggerId) => {
        setDirty(true);
        setTriggers(prev => prev.filter(t => t.trigger_id !== triggerId));
    };

    const toggleApprovalTool = (toolName) => {
        setDirty(true);
        setGovernance(prev => {
            const current = prev.requires_approval || [];
            const updated = current.includes(toolName)
                ? current.filter(t => t !== toolName)
                : [...current, toolName];
            return { ...prev, requires_approval: updated };
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    // Group tools by category
    const toolsByCategory = availableTools.reduce((acc, tool) => {
        const cat = tool.category || 'Other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(tool);
        return acc;
    }, {});

    // Group collections by category
    const collectionsByCategory = DATA_COLLECTIONS.reduce((acc, col) => {
        if (!acc[col.category]) acc[col.category] = [];
        acc[col.category].push(col);
        return acc;
    }, {});

    return (
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/agents')} className="p-2 rounded-lg hover:bg-stone-100 transition-colors">
                        <ArrowLeft className="w-5 h-5 text-stone-500" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-purple-50 border border-violet-200 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-1">
                            <input
                                value={name}
                                onChange={e => { setName(e.target.value); setDirty(true); }}
                                className="text-xl font-bold text-stone-900 bg-transparent border-none outline-none focus:ring-0 p-0 flex-1"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                                placeholder="Agent Name"
                            />
                            <VoiceButton onTranscript={t => { setName(prev => (prev + ' ' + t).trim()); setDirty(true); }} />
                        </div>
                        <div className="flex items-center gap-1">
                            <input
                                value={description}
                                onChange={e => { setDescription(e.target.value); setDirty(true); }}
                                className="text-sm text-stone-500 bg-transparent border-none outline-none focus:ring-0 p-0 block flex-1"
                                placeholder="Add a description..."
                            />
                            <VoiceButton onTranscript={t => { setDescription(prev => (prev + ' ' + t).trim()); setDirty(true); }} />
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {dirty && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full">Unsaved changes</span>
                    )}
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        agent?.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        agent?.status === 'sandbox' ? 'bg-amber-100 text-amber-700' :
                        agent?.status === 'paused' ? 'bg-blue-100 text-blue-600' :
                        'bg-stone-100 text-stone-500'
                    }`}>
                        {agent?.status || 'draft'}
                    </span>
                    <Button onClick={saveAgent} disabled={saving || !dirty} className="gap-2">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        Save
                    </Button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-1 border-b border-stone-200">
                {TABS.map(tab => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.id
                                    ? 'border-stone-900 text-stone-900'
                                    : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                            }`}
                        >
                            <Icon className="w-4 h-4" /> {tab.label}
                        </button>
                    );
                })}
            </div>

            {/* Tab Content */}
            <div className="min-h-[500px]">
                {/* ── Soul Tab ──────────────────────────────── */}
                {activeTab === 'soul' && (
                    <div className="space-y-4">
                        <div className="flex items-start justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    Soul File
                                </h2>
                                <p className="text-sm text-stone-500 mt-1">
                                    Define your agent's personality, goals, constraints, and behavioral guidelines. This is the core instruction set the agent follows.
                                </p>
                            </div>
                            <Button
                                onClick={generateSoulWithAI}
                                disabled={generating}
                                variant="outline"
                                className="gap-2 border-violet-200 text-violet-700 hover:bg-violet-50"
                            >
                                {generating ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Sparkles className="w-4 h-4" />
                                )}
                                {generating ? 'Generating...' : 'Magic AI'}
                            </Button>
                        </div>

                        {/* Model config */}
                        <div className="flex items-center gap-4 p-3 bg-stone-50 border border-stone-200 rounded-xl">
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-stone-600">Model</label>
                                <select
                                    value={modelConfig.model}
                                    onChange={e => { setModelConfig(prev => ({ ...prev, model: e.target.value })); setDirty(true); }}
                                    className="text-xs bg-white border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-300"
                                >
                                    <option value="gpt-4o">GPT-4o</option>
                                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-stone-600">Temperature</label>
                                <input
                                    type="number"
                                    min="0" max="2" step="0.1"
                                    value={modelConfig.temperature}
                                    onChange={e => { setModelConfig(prev => ({ ...prev, temperature: parseFloat(e.target.value) })); setDirty(true); }}
                                    className="w-16 text-xs bg-white border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-300"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-stone-600">Max Tokens</label>
                                <input
                                    type="number"
                                    min="256" max="16384" step="256"
                                    value={modelConfig.max_tokens}
                                    onChange={e => { setModelConfig(prev => ({ ...prev, max_tokens: parseInt(e.target.value) })); setDirty(true); }}
                                    className="w-20 text-xs bg-white border border-stone-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-300"
                                />
                            </div>
                        </div>

                        <SoulEditor
                            value={soulMd}
                            onChange={v => { setSoulMd(v); setDirty(true); }}
                        />
                    </div>
                )}

                {/* ── Tools Tab ─────────────────────────────── */}
                {activeTab === 'tools' && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Available Tools
                            </h2>
                            <p className="text-sm text-stone-500 mt-1">
                                Select the tools this agent can use. Each tool requires matching data access permissions.
                            </p>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-stone-500">
                            <span className="font-medium">{enabledTools.length}</span> tools enabled
                            <span className="text-stone-300">•</span>
                            <span className="font-medium">{availableTools.length}</span> available
                        </div>

                        {Object.entries(toolsByCategory).map(([category, tools]) => (
                            <div key={category} className="space-y-2">
                                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">{category}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {tools.map(tool => {
                                        const enabled = enabledTools.includes(tool.name);
                                        return (
                                            <button
                                                key={tool.name}
                                                onClick={() => toggleTool(tool.name)}
                                                className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                                                    enabled
                                                        ? 'border-violet-200 bg-violet-50/50'
                                                        : 'border-stone-200 bg-white hover:bg-stone-50'
                                                }`}
                                            >
                                                <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                                    enabled ? 'bg-violet-500 text-white' : 'bg-stone-100 border border-stone-300'
                                                }`}>
                                                    {enabled && <CheckCircle2 className="w-3.5 h-3.5" />}
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <code className="text-xs font-mono font-semibold text-stone-800">{tool.name}</code>
                                                        {tool.sensitive && (
                                                            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                                                                SENSITIVE
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">
                                                        {tool.description}
                                                    </p>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        {availableTools.length === 0 && (
                            <div className="text-center py-12 text-stone-400">
                                <Wrench className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                                <p className="text-sm">No tools available</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Data Access Tab ──────────────────────── */}
                {activeTab === 'data' && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Data Access
                            </h2>
                            <p className="text-sm text-stone-500 mt-1">
                                Control which CRM/ERP data this agent can read and write. Agents only access data you explicitly allow.
                            </p>
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-700">
                                Write access allows the agent to create and modify records. In sandbox mode, all writes are simulated — no real data is changed.
                            </p>
                        </div>

                        {Object.entries(collectionsByCategory).map(([category, collections]) => (
                            <div key={category} className="space-y-2">
                                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">{category}</h3>
                                <div className="space-y-1">
                                    {collections.map(col => {
                                        const hasRead = dataAccess.read?.includes(col.name);
                                        const hasWrite = dataAccess.write?.includes(col.name);
                                        return (
                                            <div key={col.name} className="flex items-center justify-between p-3 bg-white border border-stone-200 rounded-xl">
                                                <div className="flex items-center gap-3">
                                                    <Database className="w-4 h-4 text-stone-400" />
                                                    <span className="text-sm font-medium text-stone-800">{col.label}</span>
                                                    <code className="text-[10px] text-stone-400 font-mono">{col.name}</code>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={() => toggleDataAccess(col.name, 'read')}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                                            hasRead
                                                                ? 'bg-emerald-100 text-emerald-700'
                                                                : 'bg-stone-100 text-stone-400 hover:text-stone-600'
                                                        }`}
                                                    >
                                                        {hasRead ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                                                        Read
                                                    </button>
                                                    <button
                                                        onClick={() => toggleDataAccess(col.name, 'write')}
                                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                                            hasWrite
                                                                ? 'bg-violet-100 text-violet-700'
                                                                : 'bg-stone-100 text-stone-400 hover:text-stone-600'
                                                        }`}
                                                    >
                                                        {hasWrite ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                                                        Write
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Triggers Tab ─────────────────────────── */}
                {activeTab === 'triggers' && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Triggers
                            </h2>
                            <p className="text-sm text-stone-500 mt-1">
                                Define when this agent should run. Triggers fire the agent's execution loop automatically.
                            </p>
                        </div>

                        {/* Active triggers */}
                        {triggers.length > 0 && (
                            <div className="space-y-2">
                                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Active Triggers</h3>
                                {triggers.map(trigger => {
                                    const triggerDef = TRIGGER_TYPES.find(t => t.value === trigger.trigger_type);
                                    return (
                                        <div key={trigger.trigger_id} className="flex items-center justify-between p-3 bg-white border border-stone-200 rounded-xl">
                                            <div className="flex items-center gap-3">
                                                <Zap className={`w-4 h-4 ${trigger.is_active ? 'text-amber-500' : 'text-stone-300'}`} />
                                                <div>
                                                    <span className="text-sm font-medium text-stone-800">
                                                        {triggerDef?.label || trigger.trigger_type}
                                                    </span>
                                                    <p className="text-[11px] text-stone-400">
                                                        {triggerDef?.description || ''}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeTrigger(trigger.trigger_id)}
                                                className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Add trigger */}
                        <div className="space-y-2">
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Add Trigger</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {TRIGGER_TYPES.filter(t => !triggers.some(tr => tr.trigger_type === t.value)).map(trigger => (
                                    <button
                                        key={trigger.value}
                                        onClick={() => addTrigger(trigger.value)}
                                        className="flex items-start gap-3 p-3 rounded-xl border border-dashed border-stone-300 bg-white hover:bg-stone-50 hover:border-stone-400 text-left transition-all"
                                    >
                                        <Zap className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <span className="text-sm font-medium text-stone-700">{trigger.label}</span>
                                            <p className="text-[11px] text-stone-400 mt-0.5">{trigger.description}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {triggers.length === 0 && (
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
                                <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-blue-700">
                                    No triggers configured. You can still run this agent manually or via the sandbox.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Governance Tab ───────────────────────── */}
                {activeTab === 'governance' && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Governance & Guardrails
                            </h2>
                            <p className="text-sm text-stone-500 mt-1">
                                Set safety limits and approval requirements to keep your agent under control.
                            </p>
                        </div>

                        {/* Rate limits */}
                        <div className="space-y-3">
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Rate Limits</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="p-4 bg-white border border-stone-200 rounded-xl">
                                    <label className="text-xs font-medium text-stone-600">Max Actions Per Run</label>
                                    <p className="text-[10px] text-stone-400 mt-0.5 mb-2">Maximum tool calls in a single execution</p>
                                    <Input
                                        type="number" min="1" max="100"
                                        value={governance.max_actions_per_run}
                                        onChange={e => { setGovernance(prev => ({ ...prev, max_actions_per_run: parseInt(e.target.value) })); setDirty(true); }}
                                    />
                                </div>
                                <div className="p-4 bg-white border border-stone-200 rounded-xl">
                                    <label className="text-xs font-medium text-stone-600">Max Runs Per Hour</label>
                                    <p className="text-[10px] text-stone-400 mt-0.5 mb-2">Limit how frequently this agent can execute</p>
                                    <Input
                                        type="number" min="1" max="1000"
                                        value={governance.max_runs_per_hour}
                                        onChange={e => { setGovernance(prev => ({ ...prev, max_runs_per_hour: parseInt(e.target.value) })); setDirty(true); }}
                                    />
                                </div>
                                <div className="p-4 bg-white border border-stone-200 rounded-xl">
                                    <label className="text-xs font-medium text-stone-600">Max Cost Per Day (USD)</label>
                                    <p className="text-[10px] text-stone-400 mt-0.5 mb-2">Auto-pause agent when daily LLM cost exceeds this</p>
                                    <Input
                                        type="number" min="0.10" max="100" step="0.10"
                                        value={governance.max_cost_per_day_usd}
                                        onChange={e => { setGovernance(prev => ({ ...prev, max_cost_per_day_usd: parseFloat(e.target.value) })); setDirty(true); }}
                                    />
                                </div>
                                <div className="p-4 bg-white border border-stone-200 rounded-xl">
                                    <label className="text-xs font-medium text-stone-600">Auto-Pause on Errors</label>
                                    <p className="text-[10px] text-stone-400 mt-0.5 mb-2">Pause agent after this many consecutive errors</p>
                                    <Input
                                        type="number" min="1" max="20"
                                        value={governance.auto_pause_on_error_count}
                                        onChange={e => { setGovernance(prev => ({ ...prev, auto_pause_on_error_count: parseInt(e.target.value) })); setDirty(true); }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Approval requirements */}
                        <div className="space-y-3">
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Require Approval</h3>
                            <p className="text-xs text-stone-500">
                                Select tools that need human approval before execution. The agent will pause and wait for review.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {enabledTools.map(toolName => {
                                    const tool = availableTools.find(t => t.name === toolName);
                                    const needsApproval = governance.requires_approval?.includes(toolName);
                                    return (
                                        <button
                                            key={toolName}
                                            onClick={() => toggleApprovalTool(toolName)}
                                            className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                                                needsApproval
                                                    ? 'border-amber-200 bg-amber-50/50'
                                                    : 'border-stone-200 bg-white hover:bg-stone-50'
                                            }`}
                                        >
                                            <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                                                needsApproval ? 'bg-amber-500 text-white' : 'bg-stone-100 border border-stone-300'
                                            }`}>
                                                {needsApproval && <Shield className="w-3.5 h-3.5" />}
                                            </div>
                                            <div>
                                                <code className="text-xs font-mono font-semibold text-stone-800">{toolName}</code>
                                                {tool?.sensitive && (
                                                    <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                                                        SENSITIVE
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {enabledTools.length === 0 && (
                                <p className="text-xs text-stone-400 italic">Enable tools in the Tools tab first.</p>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Sandbox Tab ──────────────────────────── */}
                {activeTab === 'sandbox' && (
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Sandbox Testing
                            </h2>
                            <p className="text-sm text-stone-500 mt-1">
                                Test your agent in a safe environment. All writes are simulated and no real data is modified.
                            </p>
                        </div>

                        {/* Run controls */}
                        <div className="p-4 bg-white border border-stone-200 rounded-xl space-y-3">
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Simulate Trigger</h3>
                            <div className="flex items-center gap-3">
                                <Input
                                    value={sandboxInput}
                                    onChange={e => setSandboxInput(e.target.value)}
                                    placeholder="Optional: describe a scenario or provide context (e.g., 'new lead from website form')..."
                                    className="flex-1"
                                />
                                <VoiceButton onTranscript={t => setSandboxInput(prev => (prev + ' ' + t).trim())} />
                                <Button
                                    onClick={runSandbox}
                                    disabled={sandboxRunning || !soulMd?.trim()}
                                    className="gap-2 bg-violet-600 hover:bg-violet-700"
                                >
                                    {sandboxRunning ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Play className="w-4 h-4" />
                                    )}
                                    {sandboxRunning ? 'Running...' : 'Run Sandbox'}
                                </Button>
                            </div>
                            {!soulMd?.trim() && (
                                <p className="text-[11px] text-amber-600">
                                    Write a soul file in the Soul tab before running sandbox tests.
                                </p>
                            )}
                        </div>

                        {/* Latest sandbox result */}
                        {sandboxRun && (
                            <div className="p-4 bg-white border border-stone-200 rounded-xl space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                                        Latest Run Result
                                    </h3>
                                    <div className="flex items-center gap-2 text-xs text-stone-400">
                                        {sandboxRun.tokens_used && (
                                            <span>{sandboxRun.tokens_used.total || 0} tokens</span>
                                        )}
                                        {sandboxRun.cost_usd != null && (
                                            <span>${sandboxRun.cost_usd.toFixed(4)}</span>
                                        )}
                                    </div>
                                </div>
                                <ReasoningTraceViewer
                                    steps={sandboxRun.steps || []}
                                    status={sandboxRun.status}
                                />
                            </div>
                        )}

                        {/* Run history */}
                        {runs.length > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                                        Run History
                                    </h3>
                                    <button onClick={loadRuns} className="text-xs text-stone-400 hover:text-stone-600 flex items-center gap-1">
                                        <RefreshCw className="w-3 h-3" /> Refresh
                                    </button>
                                </div>
                                <div className="space-y-1">
                                    {runs.slice(0, 10).map(run => (
                                        <RunRow key={run.run_id} run={run} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function RunRow({ run }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border border-stone-200 rounded-xl overflow-hidden">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-3 bg-white hover:bg-stone-50 transition-colors text-left"
            >
                <div className="flex items-center gap-3">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        run.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        run.status === 'failed' ? 'bg-red-100 text-red-700' :
                        run.status === 'running' ? 'bg-blue-100 text-blue-700 animate-pulse' :
                        'bg-stone-100 text-stone-600'
                    }`}>
                        {run.status}
                    </span>
                    {run.sandbox && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                            SANDBOX
                        </span>
                    )}
                    <span className="text-xs text-stone-600">
                        {run.steps?.length || 0} steps
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="text-[11px] text-stone-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {run.started_at ? new Date(run.started_at).toLocaleString() : '—'}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-stone-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </div>
            </button>
            {expanded && (
                <div className="p-4 border-t border-stone-100 bg-stone-50/50">
                    <ReasoningTraceViewer steps={run.steps || []} status={run.status} />
                </div>
            )}
        </div>
    );
}
